import { faTablePivot } from '@fortawesome/pro-light-svg-icons/faTablePivot';
import { faGripDots } from '@fortawesome/pro-regular-svg-icons/faGripDots';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Layout, Responsive, ResponsiveProps as ResponsiveGridLayoutProps, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { GridPivotModel } from '@mui/x-data-grid-premium';
import { QBox, QPaper, QTypography, QIconButton, QLoader, useTheme } from '@captivateiq/quotient';

import {
  DASHBOARD_COLS,
  DASHBOARD_COLS_BREAKPOINTS,
  SUPPORTED_CHART_TYPES,
  ERROR_MESSAGES,
  DEFAULT_VISUALIZATION_HEIGHT,
  CHART_REGISTRY,
} from './constants';
import type { SupportedChartType } from './constants';

import { DashboardLayoutProps, VisualizationItem, ChartClientState } from '../../types';
import {
  buildLayouts,
  compareLgLayouts,
  findColumnKey,
  extractFieldName,
  formatFieldNameWithAggregation,
} from '../../utils/chartUtils';
import { VisualizationActionsMenu } from '../VisualizationActionsMenu';

const ResponsiveGridLayout = WidthProvider(Responsive);

type LineChartExtraProps = {
  xAxisDateFormat?: (value: string | Date) => string;
  showDots?: boolean | 'auto';
};

const buildLineChartExtras = (
  displaySettings: { hide_dots?: boolean } | undefined,
  dateFormatter: ((value: string | Date) => string) | undefined,
): LineChartExtraProps => ({
  ...(dateFormatter && { xAxisDateFormat: dateFormatter }),
  ...(displaySettings?.hide_dots && { showDots: false }),
});

type ComboRangeBandsConfig = {
  range_bands?: { lower_column_uuid: string; upper_column_uuid: string; color?: string }[];
};

type ComboChartExtraProps = {
  xAxisDateFormat?: (value: string | Date) => string;
  showDots?: boolean | 'auto';
  seriesStyles?: Record<string, 'line' | 'area' | 'band_bound'>;
  rangeBands?: { lowerKey: string; upperKey: string; color?: string }[];
};

// Minimal shape of a field that buildComboChartExtras actually reads. Narrower than the
// surrounding file's `any[]` so a future change to field shape (renamed `chart_style`, nested
// config, etc.) errors at compile time instead of silently downgrading combo series to lines.
type ComboFieldShape = {
  field_uuid?: string;
  aggregation?: string;
  field_config?: { data_key?: string; chart_style?: 'line' | 'area' | 'band_bound' | string };
};

/**
 * Derives the line/area/band_bound styles and range-band data keys for a combo chart.
 *
 * seriesStyles must be keyed by the same string that ends up in `yAxisKeys` (which is what the
 * chart looks up at render time). Both sides use `findColumnKey` so the resolver is shared:
 * exact match on `data_key` first, with the `_<aggregation>` suffix fallback only if the bare
 * key isn't present in the data. Reimplementing the suffix rule here would silently mis-key
 * styles on payloads where the projection kept the bare key.
 *
 * rangeBands maps `lower/upper_column_uuid` back to the chart's data keys via the
 * field-uuid → resolved-data-key lookup. Entries that can't be resolved are skipped with a
 * structured warning. Defensive — fires only on visualizations persisted before this PR's
 * server validator; steady-state, the backend rejects payloads that would produce this.
 */
const buildComboChartExtras = (
  displaySettings: (ComboRangeBandsConfig & { hide_dots?: boolean }) | undefined,
  dateFormatter: ((value: string | Date) => string) | undefined,
  fields: ComboFieldShape[] | undefined,
  dataKeys: string[],
  visualizationId: string,
): ComboChartExtraProps => {
  const seriesStyles: Record<string, 'line' | 'area' | 'band_bound'> = {};
  const fieldUuidToDataKey: Record<string, string> = {};

  (fields ?? []).forEach((field) => {
    const dataKey = findColumnKey(dataKeys, field);
    const chartStyle = field?.field_config?.chart_style;
    if (!dataKey) return;
    if (chartStyle === 'line' || chartStyle === 'area' || chartStyle === 'band_bound') {
      seriesStyles[dataKey] = chartStyle;
    }
    if (field?.field_uuid) {
      fieldUuidToDataKey[String(field.field_uuid)] = dataKey;
    }
  });

  const rangeBands = (displaySettings?.range_bands ?? [])
    .map((band) => {
      const lowerKey = fieldUuidToDataKey[band.lower_column_uuid];
      const upperKey = fieldUuidToDataKey[band.upper_column_uuid];
      if (!lowerKey || !upperKey) {
        // eslint-disable-next-line no-console
        console.warn('combo_chart: skipping unresolvable range band', {
          visualization_uuid: visualizationId,
          lower_column_uuid: band.lower_column_uuid,
          upper_column_uuid: band.upper_column_uuid,
        });
        return null;
      }
      return { lowerKey, upperKey, ...(band.color && { color: band.color }) };
    })
    .filter((entry): entry is { lowerKey: string; upperKey: string; color?: string } => entry !== null);

  return {
    ...(dateFormatter && { xAxisDateFormat: dateFormatter }),
    ...(displaySettings?.hide_dots && { showDots: false }),
    ...(Object.keys(seriesStyles).length > 0 && { seriesStyles }),
    ...(rangeBands.length > 0 && { rangeBands }),
  };
};

const buildTypeSpecificExtras = (
  chartType: string,
  displaySettings: any,
  dateFormatter: ((value: string | Date) => string) | undefined,
  fields: ComboFieldShape[] | undefined,
  dataKeys: string[],
  visualizationId: string,
): LineChartExtraProps | ComboChartExtraProps | Record<string, never> => {
  if (chartType === 'line_chart') {
    return buildLineChartExtras(displaySettings, dateFormatter);
  }
  if (chartType === 'combo_chart') {
    return buildComboChartExtras(displaySettings, dateFormatter, fields, dataKeys, visualizationId);
  }
  return {};
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  visualizations,
  dashboard,
  onLayoutSave,
  isDragEnabled = true,
  isResizeEnabled = true,
  visualizationActions = [],
  onPivotConfigSave,
  dateFormatter,
}) => {
  const theme = useTheme();
  const [currentBreakpoint, setCurrentBreakpoint] = useState<string>('lg');

  // State to track client-side only interactions for table charts (matching T&Q pattern)
  const [clientSideState, setClientSideState] = useState<Record<string, ChartClientState>>({});

  // Build layouts from visualization positions
  const layouts = useMemo<ResponsiveGridLayoutProps['layouts']>(() => buildLayouts(visualizations), [visualizations]);

  // Keep track of current lg layout for comparison
  const currentLgLayoutRef = useRef<Layout[] | null>(null);

  // Initialize the current layout reference
  if (!currentLgLayoutRef.current && layouts?.lg) {
    currentLgLayoutRef.current = layouts.lg;
  }

  // Only enable dragging/resizing on lg breakpoint
  const isDragAndResizeEnabled = isDragEnabled && isResizeEnabled && currentBreakpoint === 'lg';

  const draggableConfig = isDragAndResizeEnabled
    ? { draggableHandle: '.draggable-handle', isDraggable: true, isResizable: true }
    : { isDraggable: false, isResizable: false };

  const getInitialChartClientState = (visualization?: VisualizationItem): ChartClientState => {
    // Load saved pivot configuration from display_settings
    const savedPivotModel = visualization?.display_settings?.table_chart_grid_pivot_model;
    const savedPivotActive = visualization?.display_settings?.table_chart_grid_pivot_active;

    return {
      tableChartPivotPanelOpen: false, // Always start with panel closed
      tableChartPivotActive: savedPivotActive ?? false,
      tableChartPivotModel: savedPivotModel || undefined,
    };
  };

  const getCurrentClientState = (visualizationId: string, visualization?: VisualizationItem) => {
    return clientSideState[visualizationId] ?? getInitialChartClientState(visualization);
  };

  const updateClientConfig = (visualizationId: string, updates: Partial<ChartClientState>) => {
    const visualization = visualizations.find((v) => v.id === visualizationId);
    setClientSideState((prevState) => ({
      ...prevState,
      [visualizationId]: {
        ...getCurrentClientState(visualizationId, visualization),
        ...updates,
      },
    }));
  };

  const togglePivotPanel = (visualizationId: string) => {
    const visualization = visualizations.find((v) => v.id === visualizationId);
    const currentState = getCurrentClientState(visualizationId, visualization);
    const newPanelState = !currentState.tableChartPivotPanelOpen;

    updateClientConfig(visualizationId, {
      tableChartPivotPanelOpen: newPanelState,
    });
  };

  const handleBreakpointChange = useCallback((breakpoint: string) => {
    setCurrentBreakpoint(breakpoint);
  }, []);

  const debouncedSave = useMemo(
    () =>
      debounce((layout: Layout[]) => {
        if (onLayoutSave) {
          onLayoutSave(layout);
        }
      }, 500),
    [onLayoutSave],
  );

  const handleLayoutChange = useCallback(
    (_layout: Layout[], allLayouts: ResponsiveGridLayoutProps['layouts']) => {
      // Only care about lg breakpoint changes
      const newLgLayout = allLayouts?.lg;
      const currentLgLayout = currentLgLayoutRef.current || undefined;

      // Compare layouts to see if there are actual changes
      if (!compareLgLayouts(currentLgLayout, newLgLayout)) {
        // Update the reference for future comparisons
        currentLgLayoutRef.current = newLgLayout || null;
        // Call debounced save if layout actually changed
        if (newLgLayout) {
          debouncedSave(newLgLayout);
        }
      }
    },
    [debouncedSave],
  );

  const buildHistogramChart = (visualization: VisualizationItem, dataKeys: string[], displaySettings?: any) => {
    // For histograms, automatically detect X-axis (bins) and Y-axis (counts) columns
    const binColumn = dataKeys.find((key) => key.endsWith('_bin'));
    const countColumn = dataKeys.find((key) => key.endsWith('_count'));

    if (!binColumn || !countColumn) {
      return null; // Invalid histogram data structure
    }

    // Extract the base field name for labeling
    // Example: "{Bike Share Training!cnt}_bin" -> "cnt"
    const baseFieldName = extractFieldName(binColumn.replace(/_bin$/, ''));

    const chartProps = {
      data: visualization.data,
      xAxisKeys: [binColumn],
      yAxisKeys: [countColumn],
      yAxisLabels: ['Frequency'],
      targetLine: displaySettings?.target_line,
      ...visualization.config,
    };

    const ChartComponent = CHART_REGISTRY.histogram.component;
    return <ChartComponent {...chartProps} />;
  };

  const buildTableChart = (visualization: VisualizationItem) => {
    // For tables, we just need dimension fields (stored as x_axis)
    const tableFields = visualization.fields!.filter((f) => f.axis === 'x_axis').sort((a, b) => a.order - b.order);

    if (tableFields.length === 0) return null;
    const ChartComponent = CHART_REGISTRY.table?.component;
    if (!ChartComponent) return null;

    const currentClientState = getCurrentClientState(visualization.id, visualization);
    const vizProps = {
      data: visualization.data,
      columns: visualization.columns || [],
      disablePivoting: false,
      pivotPanelOpen: currentClientState.tableChartPivotPanelOpen ?? false,
      pivotModel: currentClientState.tableChartPivotModel,
      pivotActive: currentClientState.tableChartPivotActive ?? false,
      onClientConfigChange: (updates: Partial<ChartClientState>) => updateClientConfig(visualization.id, updates),
      onPivotConfigSave: onPivotConfigSave
        ? (payload: { table_chart_grid_pivot_model: GridPivotModel; table_chart_grid_pivot_active: boolean }) =>
            onPivotConfigSave(visualization.id, payload)
        : undefined,
    };
    return <ChartComponent {...vizProps} />;
  };

  const buildChartFromFields = (visualization: VisualizationItem, chartType: string, displaySettings?: any) => {
    const dataKeys = Object.keys(visualization.data[0] || {}).filter((key) => key !== 'index');

    if (chartType === 'table') {
      return buildTableChart(visualization);
    }

    // Special handling for histograms - they auto-generate X-axis (bins) and Y-axis (counts)
    if (chartType === 'histogram') {
      return buildHistogramChart(visualization, dataKeys, displaySettings);
    }

    // Special handling for summary cards - they only need a single metric value
    if (chartType === 'summary_card') {
      const yAxisField = visualization.fields!.find((f) => f.axis === 'y_primary');
      const xAxisField = visualization.fields!.find((f) => f.axis === 'x_axis');

      if (!yAxisField) {
        return null;
      }

      const ChartComponent = CHART_REGISTRY[chartType as SupportedChartType]?.component;
      if (!ChartComponent) {
        return null;
      }

      // Extract X-axis field name from the data key
      const xAxisLabel = xAxisField?.field_config?.data_key
        ? extractFieldName(xAxisField.field_config.data_key)
        : 'Category';

      const chartProps = {
        title: visualization.title,
        data: visualization.data, // Pass the full data structure (enhanced or standard)
        format: yAxisField.field_config?.format,
        sparkline: visualization.display_settings?.sparkline,
        xAxisLabel, // Pass the extracted X-axis field name
      };

      return <ChartComponent {...chartProps} />;
    }

    const xAxisFields = visualization.fields!.filter((f) => f.axis === 'x_axis').sort((a, b) => a.order - b.order);

    const yAxisFields = visualization
      .fields!.filter((f) => f.axis === 'y_primary' || f.axis === 'y_secondary')
      .sort((a, b) => a.order - b.order);

    if (xAxisFields.length === 0 || yAxisFields.length === 0) {
      return null;
    }

    // Extract groupBy from field configuration
    const groupBy: Array<{ key: string; keyType: string }> = [];

    visualization.fields!.forEach((field) => {
      field.field_config?.group_by?.forEach((groupConfig) => {
        if (groupConfig.column_key) {
          groupBy.push({
            key: groupConfig.column_key,
            keyType: 'string',
          });
        }
      });
    });

    // For stacking, we also need to identify the groupBy field from the stacking configuration
    // This creates the individual stack segments
    const hasStacking = yAxisFields.some((field) => field.field_config?.stack_group);

    const xAxisKey = findColumnKey(dataKeys, xAxisFields[0]);
    const yAxisKeys = yAxisFields.map((field) => findColumnKey(dataKeys, field)).filter(Boolean) as string[];

    if (!xAxisKey || yAxisKeys.length === 0) {
      return null; // Missing column keys
    }

    // Generate labels that include both column name and aggregation method
    // Example: "Payout Amt (Sum)", "Payout Amt (Average)"
    const yAxisLabels = yAxisFields.map((field, index) => {
      const columnKey = yAxisKeys[index] || '';
      return formatFieldNameWithAggregation(columnKey, field.aggregation);
    });

    // Generate X-axis labels in the same way as Y-axis labels
    const xAxisLabels = xAxisFields.map((field) => {
      return formatFieldNameWithAggregation(xAxisKey || '', field.aggregation);
    });

    // Build stackGroups for stacking - let the frontend prepareGroupedChart handle the transformation
    const stackGroups: string[][] = [];

    if (hasStacking) {
      // For stacking, we just pass the original yAxisKeys and let prepareGroupedChart create the dynamic keys
      stackGroups.push(yAxisKeys);
    }

    if (!SUPPORTED_CHART_TYPES.includes(chartType as SupportedChartType)) {
      return null;
    }

    const chartConfig = CHART_REGISTRY[chartType as SupportedChartType];
    if (!chartConfig) {
      return null;
    }

    const baseChartProps = {
      data: visualization.data,
      xAxisKeys: [xAxisKey],
      xAxisLabels,
      yAxisKeys,
      yAxisLabels,
      targetLine: displaySettings?.target_line,
      ...visualization.config,
    };

    // Add optional features based on chart capabilities. Per-chart-type extras are dispatched in
    // a single call to keep this function under the cognitive-complexity budget.
    const typeExtras = buildTypeSpecificExtras(
      chartType,
      displaySettings,
      dateFormatter,
      visualization.fields,
      dataKeys,
      visualization.id,
    );
    const chartProps = {
      ...baseChartProps,
      // For stacking: pass both groupBy (to trigger transformation) and stackGroups (to enable stacking)
      // For regular grouping: pass groupBy only (no stackGroups)
      // For regular bars: pass neither
      ...(chartConfig.supportsGrouping && groupBy.length > 0 && { groupBy }),
      ...(chartConfig.supportsStacking && stackGroups.length > 0 && { stackGroups }),
      ...typeExtras,
    };

    const ChartComponent = chartConfig.component;
    return <ChartComponent {...chartProps} />;
  };

  const renderErrorState = (title: string, message: string) => (
    <QBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: DEFAULT_VISUALIZATION_HEIGHT,
        textAlign: 'center',
        color: theme.palette.text.secondary,
      }}
    >
      <QTypography sx={{ mb: 1 }} variant="smallHeading">
        {title}
      </QTypography>
      <QTypography variant="bodyRegular">{message}</QTypography>
    </QBox>
  );

  const renderVisualization = (visualization: VisualizationItem) => {
    if (visualization.isLoading) {
      return (
        <QBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: DEFAULT_VISUALIZATION_HEIGHT,
          }}
        >
          <QLoader size="40px" />
        </QBox>
      );
    }

    const dashboardCard = dashboard?.cards?.find((card: any) => card.visualization_id === visualization.id);
    const displaySettings = dashboardCard?.visualization?.display_settings;

    if (!SUPPORTED_CHART_TYPES.includes(visualization.type as SupportedChartType)) {
      return renderErrorState(
        ERROR_MESSAGES.UNSUPPORTED_CHART_TYPE.title,
        ERROR_MESSAGES.UNSUPPORTED_CHART_TYPE.getMessage(visualization.type),
      );
    }

    if (visualization.data.length === 0) {
      return renderErrorState(ERROR_MESSAGES.NO_DATA_AVAILABLE.title, ERROR_MESSAGES.NO_DATA_AVAILABLE.message);
    }

    if (!visualization.fields || visualization.fields.length === 0) {
      return renderErrorState(
        ERROR_MESSAGES.CONFIGURATION_REQUIRED.title,
        ERROR_MESSAGES.CONFIGURATION_REQUIRED.message,
      );
    }

    const chart = buildChartFromFields(visualization, visualization.type, displaySettings);
    if (chart) return chart;

    return renderErrorState(
      ERROR_MESSAGES.CHART_CONFIGURATION_ERROR.title,
      ERROR_MESSAGES.CHART_CONFIGURATION_ERROR.message,
    );
  };

  return (
    <QBox
      sx={{
        position: 'relative',
        width: '100%',
        marginTop: '16px',
        paddingRight: '24px',
        marginBottom: '16px',
        '.react-grid-item.react-grid-placeholder': {
          zIndex: 2,
          backgroundColor: 'primary.main',
          opacity: 0.2,
          transitionDuration: '100ms',
          userSelect: 'none',
        },
      }}
    >
      <ResponsiveGridLayout
        breakpoints={DASHBOARD_COLS_BREAKPOINTS}
        cols={DASHBOARD_COLS}
        containerPadding={[0, 0]}
        layouts={layouts}
        margin={[16, 16]}
        measureBeforeMount={false}
        rowHeight={75}
        useCSSTransforms
        onBreakpointChange={handleBreakpointChange}
        onLayoutChange={handleLayoutChange}
        {...draggableConfig}
      >
        {visualizations.map((visualization) => (
          <QPaper
            elevation={0}
            key={visualization.id}
            sx={{
              border: `1px solid ${theme.palette.neutral.primary[400]}`,
              borderRadius: '8px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              p: 3,
              marginLeft: '10px',
              marginRight: '10px',
              wordBreak: 'break-word',
              ...(isDragAndResizeEnabled && {
                '&:hover .draggable-handle': {
                  display: 'flex',
                },
              }),
            }}
          >
            {isDragAndResizeEnabled && (
              <QBox
                className="draggable-handle"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  left: 0,
                  display: 'none',
                  justifyContent: 'center',
                  width: '100%',
                  p: 0.5,
                  borderTopLeftRadius: 1,
                  borderTopRightRadius: 1,
                  cursor: 'grab',
                  '&:hover svg': {
                    color: 'text.primary',
                  },
                }}
              >
                <FontAwesomeIcon
                  icon={faGripDots}
                  style={{
                    color: 'inherit',
                    fontSize: '16px',
                  }}
                />
              </QBox>
            )}
            {/* Header with title and actions */}
            <QBox
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 2,
                gap: 1,
              }}
            >
              <QTypography
                component="h3"
                sx={{
                  flexGrow: 1,
                  textAlign: 'left',
                  wordBreak: 'break-word',
                  minWidth: 0, // Allow text to shrink
                }}
                variant="mediumHeading"
              >
                {visualization.title}
              </QTypography>
              <QBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Add pivot button for table charts */}
                {isDragAndResizeEnabled && visualization.type === 'table' && (
                  <QIconButton
                    size="small"
                    sx={{
                      color: getCurrentClientState(visualization.id, visualization).tableChartPivotPanelOpen
                        ? 'accent.700'
                        : 'neutral.primary.600',
                    }}
                    onClick={() => togglePivotPanel(visualization.id)}
                  >
                    <FontAwesomeIcon icon={faTablePivot} style={{ fontSize: '16px' }} />
                  </QIconButton>
                )}
                {visualizationActions.length > 0 && (
                  <VisualizationActionsMenu actions={visualizationActions} item={visualization} />
                )}
              </QBox>
            </QBox>
            <QBox sx={{ flex: 1, minHeight: 0 }}>{renderVisualization(visualization)}</QBox>
          </QPaper>
        ))}
      </ResponsiveGridLayout>
    </QBox>
  );
};
